import React from 'react';
import { Plus, X, ArrowLeftRight } from 'lucide-react';

export interface DataRow {
  label: string;
  [key: string]: string; // Dynamic columns for series (e.g., Sales: "100", Revenue: "1000")
}

interface ChartDataTableProps {
  rows: DataRow[];
  series: string[]; // Column names ["Sales", "Revenue", "Profit"]
  seriesColors: Record<string, string>; // { "Sales": "#8b5cf6", "Revenue": "#3b82f6" }
  onRowsChange: (rows: DataRow[]) => void;
  onSeriesChange: (series: string[]) => void;
  onSeriesColorsChange: (colors: Record<string, string>) => void;
  maxRows?: number;
  maxSeries?: number;
  warningThreshold?: number;
}

export function ChartDataTable({
  rows,
  series,
  seriesColors,
  onRowsChange,
  onSeriesChange,
  onSeriesColorsChange,
  maxRows = 1000,
  maxSeries = 10,
  warningThreshold = 500,
}: ChartDataTableProps) {
  const defaultColors = ['#8b5cf6', '#3b82f6', '#10b981', '#ef4444', '#f97316', '#ec4899', '#14b8a6', '#f59e0b'];
  const handleLabelChange = (rowIndex: number, value: string) => {
    const newRows = [...rows];
    newRows[rowIndex].label = value;
    onRowsChange(newRows);
  };

  const handleCellChange = (rowIndex: number, seriesName: string, value: string) => {
    const newRows = [...rows];
    newRows[rowIndex][seriesName] = value;
    onRowsChange(newRows);
  };

  const handleAddRow = () => {
    if (rows.length >= maxRows) return;
    const newRow: DataRow = { label: '' };
    series.forEach(s => newRow[s] = '');
    onRowsChange([...rows, newRow]);
  };

  const handleDeleteRow = (index: number) => {
    if (rows.length <= 1) return;
    onRowsChange(rows.filter((_, i) => i !== index));
  };

  const handleAddSeries = () => {
    if (series.length >= maxSeries) return;
    const newSeriesName = `Series ${series.length + 1}`;
    onSeriesChange([...series, newSeriesName]);
    // Assign default color to new series
    const newColors = { ...seriesColors, [newSeriesName]: defaultColors[series.length % defaultColors.length] };
    onSeriesColorsChange(newColors);
    // Initialize all rows with empty value for new series
    const newRows = rows.map(row => ({ ...row, [newSeriesName]: '' }));
    onRowsChange(newRows);
  };

  const handleSeriesColorChange = (seriesName: string, color: string) => {
    onSeriesColorsChange({ ...seriesColors, [seriesName]: color });
  };

  const handleDeleteSeries = (seriesName: string) => {
    if (series.length <= 1) return; // Must have at least 1 series
    onSeriesChange(series.filter(s => s !== seriesName));
    // Remove series from all rows
    const newRows = rows.map(row => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [seriesName]: _, ...rest } = row;
      return rest as DataRow;
    });
    onRowsChange(newRows);
  };

  const handleSeriesRename = (oldName: string, newName: string) => {
    if (!newName.trim()) return; // Don't allow empty names
    // Allow duplicate names during typing, only check on blur
    const newSeries = series.map(s => s === oldName ? newName : s);
    onSeriesChange(newSeries);
    // Rename series in all rows
    const newRows = rows.map(row => {
      const { [oldName]: value, ...rest } = row;
      return { ...rest, [newName]: value } as DataRow;
    });
    onRowsChange(newRows);
  };

  const handleTranspose = () => {
    // Transpose: swap rows and columns
    // Current: rows=[{label:"Jan", Sales:100, Revenue:1000}], series=["Sales","Revenue"]
    // After: rows=[{label:"Sales", Jan:100, Feb:150}], series=["Jan","Feb"]

    const newSeries = rows.map(row => row.label).filter(Boolean);
    const newRows: DataRow[] = series.map(seriesName => {
      const newRow: DataRow = { label: seriesName };
      rows.forEach(row => {
        if (row.label) {
          newRow[row.label] = row[seriesName] || '';
        }
      });
      return newRow;
    });

    onSeriesChange(newSeries);
    onRowsChange(newRows);
  };

  const showWarning = rows.length > warningThreshold;
  const atMaxRows = rows.length >= maxRows;
  const atMaxSeries = series.length >= maxSeries;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">
          Data Table *
        </label>
        <button
          onClick={handleTranspose}
          className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-all"
          title="Swap rows and columns"
        >
          <ArrowLeftRight size={14} />
          Transpose
        </button>
      </div>

      {/* Warning for many rows */}
      {showWarning && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-2 text-xs text-yellow-800 dark:text-yellow-200">
          ⚠️ Performance may degrade with &gt;{warningThreshold} rows
        </div>
      )}

      {/* Table - Scrollable horizontally and vertically */}
      <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-auto max-h-72">
        <table className="w-full text-xs">
          {/* Header */}
          <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0 z-10">
            <tr>
              <th className="px-2 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 border-r border-gray-300 dark:border-gray-600">
                Label
              </th>
              {series.map((seriesName, index) => (
                <th key={seriesName} className="px-2 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 border-r border-gray-300 dark:border-gray-600">
                  <div className="flex items-center gap-1">
                    <input
                      type="color"
                      value={seriesColors[seriesName] || defaultColors[index % defaultColors.length]}
                      onChange={(e) => handleSeriesColorChange(seriesName, e.target.value)}
                      className="w-6 h-6 rounded cursor-pointer border border-gray-400 dark:border-gray-500 flex-shrink-0"
                      title="Click to change color"
                    />
                    <input
                      type="text"
                      value={seriesName}
                      onChange={(e) => handleSeriesRename(seriesName, e.target.value)}
                      onFocus={(e) => e.target.select()}
                      className="flex-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 font-semibold text-gray-900 dark:text-white hover:border-purple-400 dark:hover:border-purple-500 focus:border-purple-600 dark:focus:border-purple-400 focus:ring-1 focus:ring-purple-600 dark:focus:ring-purple-400 outline-none min-w-0"
                      maxLength={50}
                      placeholder="Column name"
                      title="Click to edit column name"
                    />
                    {series.length > 1 && (
                      <button
                        onClick={() => handleDeleteSeries(seriesName)}
                        className="text-red-600 hover:text-red-700 flex-shrink-0"
                        title="Delete column"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </th>
              ))}
              <th className="px-2 py-2 w-10"></th>
            </tr>
          </thead>

          {/* Rows */}
          <tbody className="bg-white dark:bg-gray-800">
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-t border-gray-200 dark:border-gray-700">
                {/* Label */}
                <td className="px-2 py-2 border-r border-gray-300 dark:border-gray-600">
                  <input
                    type="text"
                    value={row.label}
                    onChange={(e) => handleLabelChange(rowIndex, e.target.value)}
                    placeholder="e.g., Jan"
                    className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    maxLength={100}
                  />
                </td>

                {/* Series Values */}
                {series.map((seriesName) => (
                  <td key={seriesName} className="px-2 py-2 border-r border-gray-300 dark:border-gray-600">
                    <input
                      type="number"
                      value={row[seriesName] || ''}
                      onChange={(e) => handleCellChange(rowIndex, seriesName, e.target.value)}
                      placeholder="0"
                      className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    />
                  </td>
                ))}

                {/* Delete Row Button */}
                <td className="px-2 py-2 text-center">
                  <button
                    onClick={() => handleDeleteRow(rowIndex)}
                    disabled={rows.length <= 1}
                    className="text-red-600 hover:text-red-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                    title={rows.length <= 1 ? 'Must have at least 1 row' : 'Delete row'}
                  >
                    <X size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <button
          onClick={handleAddRow}
          disabled={atMaxRows}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-xs rounded-lg font-semibold transition-all"
          title={atMaxRows ? `Maximum ${maxRows} rows reached` : 'Add new row'}
        >
          <Plus size={14} />
          Add Row
        </button>

        <button
          onClick={handleAddSeries}
          disabled={atMaxSeries}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-xs rounded-lg font-semibold transition-all"
          title={atMaxSeries ? `Maximum ${maxSeries} series reached` : 'Add new column'}
        >
          <Plus size={14} />
          Add Column
        </button>
      </div>

      {/* Stats */}
      <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
        {rows.length} row{rows.length !== 1 ? 's' : ''} × {series.length} column{series.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
